import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Link from "@mui/material/Link";
import type { ChipProps } from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { isApiError } from "@bw/shared/api";

import { getTournamentCloseout, type Tournament } from "@/libs/tournamentApi";
import {
  getTournamentSanction,
  requestTournamentSanction,
  submitTournamentSanctionResults,
  withdrawTournamentSanction,
  type TournamentSanction,
  type TournamentSanctionState,
  type TournamentSanctionStatus,
} from "@/libs/tournamentSanctionApi";

const STATUS: Record<
  TournamentSanctionStatus,
  { label: string; color: ChipProps["color"] }
> = {
  requested: { label: "In review", color: "warning" },
  changes_requested: { label: "Changes requested", color: "info" },
  rejected: { label: "Not approved", color: "error" },
  withdrawn: { label: "Withdrawn", color: "default" },
  sanctioned: { label: "Official BW event", color: "success" },
  results_submitted: { label: "Results submitted", color: "success" },
  ratings_applied: { label: "Ratings applied", color: "success" },
  completed_unrated: { label: "Closed without ratings", color: "default" },
  revoked: { label: "Revoked", color: "error" },
};

const DATE_TIME = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
});
const fmt = (iso: string | null | undefined) =>
  iso ? DATE_TIME.format(new Date(iso)) : "-";

class SanctionCardError extends Error {}

/** Tournament tabs the card links to. */
export type SanctionTab = "closeout" | "staff" | "events";

function TabLink({
  tab,
  label,
  onOpenTab,
}: {
  tab: SanctionTab;
  label: string;
  onOpenTab: (tab: SanctionTab) => void;
}) {
  return (
    <Link
      component="button"
      type="button"
      variant="body2"
      onClick={() => onOpenTab(tab)}
      sx={{ verticalAlign: "baseline" }}
    >
      {label}
    </Link>
  );
}

type SanctionAction =
  | { kind: "request"; note: string | null }
  | { kind: "withdraw" }
  | { kind: "results" };

const SUCCESS: Record<SanctionAction["kind"], string> = {
  request:
    "Request sent. Badminton Warriors will let you know when it's reviewed.",
  withdraw: "Sanction request withdrawn.",
  results: "Results submitted to Badminton Warriors.",
};

function errorText(error: unknown): string {
  if (error instanceof SanctionCardError) return error.message;
  if (isApiError(error) && error.message) return error.message;
  return "That didn't work. Try again.";
}

function Note({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Alert severity="info" icon={false} sx={{ py: 0.25 }}>
      <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
        {title}:
      </Typography>{" "}
      <Typography component="span" variant="body2">
        {children}
      </Typography>
    </Alert>
  );
}

function Step({
  done,
  label,
  detail,
}: {
  done: boolean;
  label: string;
  detail: string;
}) {
  const Icon = done ? CheckCircleIcon : RadioButtonUncheckedIcon;
  return (
    <Stack
      direction="row"
      spacing={0.75}
      sx={{ alignItems: "center", minWidth: 0 }}
    >
      <Icon
        sx={{ fontSize: 16, color: done ? "success.main" : "text.disabled" }}
        aria-hidden="true"
      />
      <Typography variant="body2" noWrap>
        <b>{label}</b>{" "}
        <Typography component="span" variant="body2" color="text.secondary">
          {detail}
        </Typography>
      </Typography>
    </Stack>
  );
}

function Progress({ sanction }: { sanction: TournamentSanction }) {
  const submitted = ["results_submitted", "ratings_applied"].includes(
    sanction.status,
  );
  const applied = sanction.status === "ratings_applied";
  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={{ xs: 0.5, md: 2.5 }}
      role="list"
      aria-label="Sanction progress"
    >
      <Box role="listitem">
        <Step done label="Requested" detail={fmt(sanction.submitted_at)} />
      </Box>
      <Box role="listitem">
        <Step done label="Sanctioned" detail={fmt(sanction.decided_at)} />
      </Box>
      <Box role="listitem">
        <Step
          done={submitted}
          label="Results"
          detail={
            submitted
              ? `${fmt(sanction.results_submitted_at)}${sanction.results_late ? " (late)" : ""}`
              : `due ${fmt(sanction.results_due_at)}`
          }
        />
      </Box>
      <Box role="listitem">
        <Step
          done={applied}
          label="BW ratings"
          detail={
            applied
              ? fmt(sanction.ratings_applied_at)
              : sanction.status === "completed_unrated"
                ? "not applied"
                : "pending"
          }
        />
      </Box>
    </Stack>
  );
}

function Body({
  tournament,
  state,
  pending,
  onRequest,
  onWithdraw,
  onSubmitResults,
  onOpenTab,
}: {
  tournament: Tournament;
  state: TournamentSanctionState;
  pending: boolean;
  onRequest: (note: string | null) => void;
  onWithdraw: () => void;
  onSubmitResults: () => void;
  onOpenTab: (tab: SanctionTab) => void;
}) {
  const [note, setNote] = useState("");
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  const current = state.current ?? null;
  const missing = state.missing_requirements ?? [];
  const status = current?.status ?? null;
  const started = new Date(tournament.start_date).getTime() <= Date.now();
  const published =
    Boolean(tournament.is_published) && tournament.status !== "draft";

  const requestForm = state.can_request ? (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      sx={{ alignItems: { sm: "flex-start" } }}
    >
      <TextField
        size="small"
        fullWidth
        label={
          status === "changes_requested"
            ? "What you changed (optional)"
            : "Note for Badminton Warriors (optional)"
        }
        value={note}
        onChange={(event) => setNote(event.target.value.slice(0, 1000))}
      />
      <Button
        variant="contained"
        startIcon={<VerifiedOutlinedIcon />}
        disabled={pending}
        onClick={() => onRequest(note.trim() || null)}
        sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
      >
        {status === "changes_requested"
          ? "Resubmit request"
          : "Request sanction"}
      </Button>
    </Stack>
  ) : null;

  const prerequisites = missing.length ? (
    <Alert severity="warning" sx={{ py: 0.25 }}>
      Before you can request sanction:
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {missing.includes("referee") ? (
          <li>
            Assign a tournament referee in{" "}
            <TabLink tab="staff" label="Staff" onOpenTab={onOpenTab} />.
          </li>
        ) : null}
        {missing.includes("event") ? (
          <li>
            Add at least one event in{" "}
            <TabLink tab="events" label="Events" onOpenTab={onOpenTab} />.
          </li>
        ) : null}
      </Box>
    </Alert>
  ) : null;

  const withdraw = state.can_withdraw ? (
    <>
      <Button
        color="inherit"
        size="small"
        disabled={pending}
        onClick={() => setConfirmWithdraw(true)}
      >
        Withdraw request
      </Button>
      <Dialog
        open={confirmWithdraw}
        onClose={() => setConfirmWithdraw(false)}
        aria-labelledby="withdraw-sanction-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="withdraw-sanction-title">
          Withdraw sanction request?
        </DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2">
            {status === "sanctioned"
              ? "The tournament stops being an official BW event and its results won't count toward BW ratings."
              : "Badminton Warriors stops reviewing it."}{" "}
            You can request again before the tournament starts.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmWithdraw(false)}>
            Keep request
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={pending}
            onClick={() => {
              setConfirmWithdraw(false);
              onWithdraw();
            }}
          >
            Withdraw
          </Button>
        </DialogActions>
      </Dialog>
    </>
  ) : null;

  if (!current || status === "withdrawn") {
    return (
      <Stack spacing={1.25}>
        <Typography variant="body2" color="text.secondary">
          Official BW events are listed as official and their results count
          toward BW ratings and rankings. Free during the pilot. Badminton
          Warriors checks your dates, venue, events, draw formats and referee.
        </Typography>
        {status === "withdrawn" ? (
          <Typography variant="body2" color="text.secondary">
            You withdrew your earlier request.
          </Typography>
        ) : null}
        {requestForm ??
          (prerequisites ? (
            prerequisites
          ) : started ? (
            <Typography variant="body2" color="text.secondary">
              Sanction must be requested before the tournament starts.
            </Typography>
          ) : !published ? (
            <Typography variant="body2" color="text.secondary">
              Publish the tournament before requesting sanction.
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              This tournament can&apos;t be sanctioned right now.
            </Typography>
          ))}
      </Stack>
    );
  }

  const conditions = current.conditions ?? [];
  return (
    <Stack spacing={1.25}>
      {status === "requested" ? (
        <Typography variant="body2" color="text.secondary">
          Badminton Warriors is reviewing your request from{" "}
          {fmt(current.submitted_at)}.
        </Typography>
      ) : null}
      {status === "changes_requested" && current.decision_note ? (
        <Note title="Changes requested">{current.decision_note}</Note>
      ) : null}
      {status === "rejected" && current.decision_note ? (
        <Note title="Reason">{current.decision_note}</Note>
      ) : null}
      {(status === "revoked" || status === "completed_unrated") &&
      current.close_note ? (
        <Note
          title={
            status === "revoked" ? "Why it was revoked" : "Why no ratings apply"
          }
        >
          {current.close_note}
        </Note>
      ) : null}
      {status === "revoked" ? (
        <Typography variant="body2" color="text.secondary">
          This tournament is no longer an official BW event and its results
          don&apos;t count toward BW ratings.
        </Typography>
      ) : null}
      {status === "completed_unrated" ? (
        <Typography variant="body2" color="text.secondary">
          It stays an official BW event, but its results don&apos;t change BW
          ratings.
        </Typography>
      ) : null}

      {status &&
      [
        "sanctioned",
        "results_submitted",
        "ratings_applied",
        "completed_unrated",
      ].includes(status) ? (
        <Progress sanction={current} />
      ) : null}
      {conditions.length ? (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Conditions
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
            {conditions.map((condition, index) => (
              <Typography
                component="li"
                variant="body2"
                key={`${index}-${condition}`}
              >
                {condition}
              </Typography>
            ))}
          </Box>
        </Box>
      ) : null}
      {status === "sanctioned" && current.returned_note ? (
        <Note title="Results returned for correction">
          {current.returned_note}
        </Note>
      ) : null}
      {status === "sanctioned" && current.results_overdue ? (
        <Alert severity="error" sx={{ py: 0.25 }}>
          Results were due {fmt(current.results_due_at)}. Submit them now;
          Badminton Warriors decides whether late results are rated.
        </Alert>
      ) : null}
      {status === "results_submitted" && current.results_stale ? (
        <Alert severity="warning" sx={{ py: 0.25 }}>
          Your results changed after you submitted them. Submit the corrected
          certified results.
        </Alert>
      ) : null}

      {status === "sanctioned" ||
      (status === "results_submitted" && current.results_stale) ? (
        state.can_submit_results ? (
          <Box>
            <Button
              variant="contained"
              disabled={pending}
              onClick={onSubmitResults}
            >
              {status === "results_submitted"
                ? "Resubmit results"
                : "Submit results to BW"}
            </Button>
          </Box>
        ) : started ? (
          <Typography variant="body2" color="text.secondary">
            Certify the final results in{" "}
            <TabLink tab="closeout" label="Closeout" onOpenTab={onOpenTab} />,
            then submit them here within 7 days of the last day.
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            After the tournament, certify results in Closeout and submit them
            within 7 days of the last day.
          </Typography>
        )
      ) : null}

      {requestForm ??
        (status === "changes_requested" ||
        status === "rejected" ||
        status === "revoked"
          ? prerequisites
          : null)}
      {withdraw ? <Box>{withdraw}</Box> : null}
    </Stack>
  );
}

/**
 * The club's official BW sanction for one tournament: request before the
 * start (published only), follow the review, withdraw, and submit certified
 * results within 7 days of the last day.
 */
export function TournamentSanctionCard({
  tournament,
  onOpenTab,
}: {
  tournament: Tournament;
  onOpenTab: (tab: SanctionTab) => void;
}) {
  const tournamentId = tournament.tournament_id;
  const [state, setState] = useState<TournamentSanctionState | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<{
    severity: "success" | "error";
    text: string;
  } | null>(null);
  const requestKey = useRef<string | null>(null);
  const resultsKey = useRef<string | null>(null);
  const generation = useRef(0);

  const load = useCallback(async () => {
    const current = ++generation.current;
    try {
      const next = await getTournamentSanction(tournamentId);
      if (current !== generation.current) return;
      setState(next);
      setLoadFailed(false);
    } catch {
      if (current !== generation.current) return;
      setLoadFailed(true);
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => {
    setState(null);
    setLoading(true);
    setMessage(null);
    requestKey.current = null;
    resultsKey.current = null;
    void load();
    return () => {
      generation.current += 1;
    };
  }, [load]);

  const run = async (action: SanctionAction) => {
    if (pending) return;
    setPending(true);
    setMessage(null);
    try {
      if (action.kind === "request") {
        requestKey.current ??= crypto.randomUUID();
        await requestTournamentSanction(tournamentId, {
          idempotency_key: requestKey.current,
          note: action.note,
        });
        requestKey.current = null;
      } else if (action.kind === "withdraw") {
        await withdrawTournamentSanction(tournamentId, {
          expected_revision: state!.current!.revision,
        });
      } else {
        const closeout = await getTournamentCloseout(tournamentId);
        if (
          !closeout.certification ||
          closeout.certification_state !== "current"
        ) {
          throw new SanctionCardError(
            "Certify the current results in Closeout before submitting them.",
          );
        }
        resultsKey.current ??= crypto.randomUUID();
        await submitTournamentSanctionResults(tournamentId, {
          certification_id: closeout.certification.certification_id,
          expected_result_revision: closeout.result_revision,
          idempotency_key: resultsKey.current,
        });
        resultsKey.current = null;
      }
      setMessage({ severity: "success", text: SUCCESS[action.kind] });
      await load();
    } catch (error) {
      if (isApiError(error) && error.status === 409) {
        // Someone else changed the sanction (or the results): show the latest state.
        requestKey.current = null;
        resultsKey.current = null;
        await load();
      }
      setMessage({ severity: "error", text: errorText(error) });
    } finally {
      setPending(false);
    }
  };

  const status = state?.current?.status;
  const visibleStatus =
    status && status !== "withdrawn" ? STATUS[status] : null;

  return (
    <Paper
      component="section"
      variant="outlined"
      aria-labelledby="tournament-sanction-title"
    >
      <Stack spacing={1.25} sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.5 }}
        >
          <VerifiedOutlinedIcon
            fontSize="small"
            color={visibleStatus?.color === "success" ? "success" : "action"}
          />
          <Typography
            id="tournament-sanction-title"
            component="h2"
            variant="subtitle1"
            sx={{ fontWeight: 700 }}
          >
            Official BW tournament
          </Typography>
          {visibleStatus ? (
            <Chip
              size="small"
              variant="outlined"
              color={visibleStatus.color}
              label={visibleStatus.label}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Request before the tournament starts
            </Typography>
          )}
          {state?.current?.results_late && status !== "sanctioned" ? (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label="Results late"
            />
          ) : null}
        </Stack>
        {message ? (
          <Alert
            severity={message.severity}
            onClose={() => setMessage(null)}
            sx={{ py: 0.25 }}
          >
            {message.text}
          </Alert>
        ) : null}
        {loading ? (
          <Box aria-busy="true" aria-label="Loading sanction">
            <Skeleton height={20} />
            <Skeleton height={20} width="60%" />
          </Box>
        ) : loadFailed || !state ? (
          <Typography variant="body2" color="text.secondary">
            Sanction status couldn&apos;t be loaded.
          </Typography>
        ) : (
          <Body
            tournament={tournament}
            state={state}
            pending={pending}
            onRequest={(note) => void run({ kind: "request", note })}
            onWithdraw={() => void run({ kind: "withdraw" })}
            onSubmitResults={() => void run({ kind: "results" })}
            onOpenTab={onOpenTab}
          />
        )}
      </Stack>
    </Paper>
  );
}
